import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { GeckoEntryBase } from 'types'

import { DrawerState } from './enums'

type RightDrawerContextType = {
  drawerState: DrawerState
  setDrawerState: Dispatch<SetStateAction<DrawerState>>
  drawerEntry: GeckoEntryBase | undefined
  setDrawerEntry: Dispatch<SetStateAction<GeckoEntryBase | undefined>>
  onDrawerClose: (() => void) | undefined
  setOnDrawerClose: Dispatch<SetStateAction<(() => void) | undefined>>
  closeDrawer: () => void
}

const RightDrawerContext = createContext<RightDrawerContextType | undefined>(
  undefined
)

export const RightDrawerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [drawerState, setDrawerState] = useState<DrawerState>(
    DrawerState.Closed
  )
  const [drawerEntry, setDrawerEntry] = useState<GeckoEntryBase | undefined>(
    undefined
  )
  const [onDrawerClose, setOnDrawerClose] = useState<(() => void) | undefined>(
    undefined
  )

  const context = useMemo<RightDrawerContextType>(() => {
    const closeDrawer = () => {
      if (onDrawerClose) {
        onDrawerClose()
      }
      setDrawerState(DrawerState.Closed)
      setDrawerEntry(undefined)
      setOnDrawerClose(undefined)
    }

    return {
      drawerState,
      setDrawerState,
      drawerEntry,
      setDrawerEntry,
      onDrawerClose,
      setOnDrawerClose,
      closeDrawer,
    }
  }, [
    drawerState,
    setDrawerState,
    drawerEntry,
    setDrawerEntry,
    onDrawerClose,
    setOnDrawerClose,
  ])

  return (
    <RightDrawerContext.Provider value={context}>
      {children}
    </RightDrawerContext.Provider>
  )
}

export const useRightDrawer = () => {
  const context = useContext(RightDrawerContext)
  if (!context) {
    throw new Error(
      'useRightDrawer() must be used within the RightDrawerProvider!'
    )
  }
  return context
}
